import { WINDOW_SIZE } from '@/constants/window-size';
import { ScreenSizeContext } from '@/contexts/ScreenSizeContext';
import { useOrientation } from '@/hooks';
import { getHeightSize, getHSize, getSize, getWidthSize } from '@/utils/size';
import React, { useCallback, useMemo } from 'react';

interface ScreenSizeProviderProps {
    children: React.ReactNode;
}

export const ScreenSizeProvider: React.FC<ScreenSizeProviderProps> = ({ children }) => {
    const {
        isHorizontal,
        dimensions: { width: currentWidth, height: currentHeight },
    } = useOrientation();

    const { baseWidth, baseHeight } = useMemo(() => {
        const { HORIZONTAL, VERTICAL } = WINDOW_SIZE;
        return isHorizontal
            ? {
                baseWidth: HORIZONTAL.WIDTH,
                baseHeight: HORIZONTAL.HEIGHT,
            }
            : {
                baseWidth: VERTICAL.WIDTH,
                baseHeight: VERTICAL.HEIGHT,
            };
    }, [isHorizontal]);

    const scaleRatioWidth = useMemo(() => {
        return currentWidth / baseWidth;
    }, [currentWidth, baseWidth]);

    const scaleRatioHeight = useMemo(() => {
        return currentHeight / baseHeight;
    }, [currentHeight, baseHeight]);

    const scaleWidth = useCallback((width?: number) => getWidthSize(width ?? 0, baseWidth), [baseWidth]);

    const scaleHeight = useCallback((height?: number) => getHeightSize(height ?? 0, baseHeight), [baseHeight]);

    const scaleWidthVw = useCallback((width?: number) => getSize(width ?? 0, baseWidth), [baseWidth]);

    const scaleHeightVh = useCallback((height?: number) => getHSize(height ?? 0, baseHeight), [baseHeight]);

    const scaleWidthPixel = useCallback((width?: number) => Math.round((width ?? 0) * scaleRatioWidth), [scaleRatioWidth]);

    const scaleHeightPixel = useCallback((height?: number) => Math.round((height ?? 0) * scaleRatioHeight), [scaleRatioHeight]);

    const value = useMemo(
        () => ({
            baseWidth,
            baseHeight,
            currentWidth,
            currentHeight,
            scaleRatioWidth,
            scaleRatioHeight,
            scaleWidth,
            scaleHeight,
            scaleWidthVw,
            scaleHeightVh,
            scaleWidthPixel,
            scaleHeightPixel,
        }),
        [
            baseWidth,
            baseHeight,
            currentWidth,
            currentHeight,
            scaleRatioWidth,
            scaleRatioHeight,
            scaleWidth,
            scaleHeight,
            scaleWidthVw,
            scaleHeightVh,
            scaleWidthPixel,
            scaleHeightPixel,
        ]
    );

    return <ScreenSizeContext.Provider value={value}>{children}</ScreenSizeContext.Provider>;
};
